import {
  CheckoutStatus,
  type CheckoutSession,
  type EarlyRepayment,
  type OverduePayments,
} from "@/types";
import { hasLatchedOverdueRepaymentFlow } from "./overdue-flow-latch";

export type RepaymentFlowMode = "early" | "overdue";

export type RepaymentFlowContext =
  | {
      mode: "early";
      earlyRepayment: EarlyRepayment;
      paymentAmount: number;
      currency: string;
    }
  | {
      mode: "overdue";
      overduePayments: OverduePayments;
      paymentAmount: number;
      currency: string;
    };

function isActiveOverdueRepayment(overdue: OverduePayments): boolean {
  return overdue.hasOverdue && overdue.payments.length > 0;
}

/** Overdue settled but payload still on session (hasOverdue may be false). */
function isSettledOverdueRepayment(
  session: CheckoutSession,
  overdue: OverduePayments,
): boolean {
  if (session.status !== CheckoutStatus.EARLY_REPAYMENT_COMPLETED) {
    return false;
  }
  if (isActiveOverdueRepayment(overdue)) {
    return false;
  }
  return (
    overdue.count > 0 ||
    overdue.payments.length > 0 ||
    overdue.totalDueAmount.amount > 0
  );
}

export function isOverdueRepaymentFlow(session: CheckoutSession): boolean {
  if (hasLatchedOverdueRepaymentFlow(session.id)) {
    return true;
  }
  return getRepaymentFlowContext(session)?.mode === "overdue";
}

export function getRepaymentFlowContext(
  session: CheckoutSession,
): RepaymentFlowContext | null {
  const overdue = session.overduePayments;
  if (
    overdue &&
    (isActiveOverdueRepayment(overdue) ||
      isSettledOverdueRepayment(session, overdue))
  ) {
    return {
      mode: "overdue",
      overduePayments: overdue,
      paymentAmount: overdue.totalDueAmount.amount,
      currency: overdue.totalDueAmount.currency,
    };
  }

  const earlyRepayment = session.earlyRepayment;
  if (earlyRepayment) {
    return {
      mode: "early",
      earlyRepayment,
      paymentAmount: earlyRepayment.amount.amount,
      currency: earlyRepayment.amount.currency,
    };
  }

  return null;
}

export function isEarlyRepaymentRouteActive(session: CheckoutSession): boolean {
  return getRepaymentFlowContext(session) != null;
}
